import { motion } from "framer-motion";
import { Building2, Landmark, MapPinned, Layers, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const ServicesPage = () => {
  const services = [
    {
      icon: Building2,
      title: "Development Finance",
      description: "End-to-end funding solutions for residential and commercial development projects.",
      features: [
        "Construction loans up to $100M+",
        "Land and construction in single facility",
        "Progress draw management",
        "Flexible repayment structures"
      ]
    },
    {
      icon: Landmark,
      title: "Construction Loans",
      description: "Tailored construction financing with competitive rates and flexible terms.",
      features: [
        "Staged drawdown facilities",
        "Interest capitalisation options",
        "QS and certification support",
        "Major bank and non-bank lenders"
      ]
    },
    {
      icon: MapPinned,
      title: "Land Acquisition",
      description: "Rapid funding for strategic land purchases and site assembly.",
      features: [
        "Fast settlement capability",
        "Bridge financing available",
        "DA and pre-DA funding",
        "Joint venture structuring"
      ]
    },
    {
      icon: Layers,
      title: "Mezzanine Funding",
      description: "Second-tier capital to optimise your equity deployment and returns.",
      features: [
        "Preferred equity structures",
        "Subordinated debt facilities",
        "Gap funding solutions",
        "Flexible security arrangements"
      ]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Initial Consultation",
      description: "Meet with a partner to discuss your project, objectives, and funding requirements."
    },
    {
      step: "02",
      title: "Feasibility Analysis",
      description: "Our team conducts thorough due diligence and prepares institutional-grade documentation."
    },
    {
      step: "03",
      title: "Lender Matching",
      description: "We identify and approach the optimal lenders for your specific project profile."
    },
    {
      step: "04",
      title: "Execution & Settlement",
      description: "Manage the process through to settlement with transparent communication throughout."
    }
  ];

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="services-page">
      <Navigation />
      
      <main className="flex-1 relative z-10 px-8 lg:px-16 pt-40 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <h1 
              className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight mb-6"
              data-testid="services-heading"
            >
              Our Services
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-slate-300">
              Comprehensive property finance solutions tailored to developers, 
              acquirers, and investors across Australia.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-20">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="glass-card p-8 rounded-sm hover:border-[#bd9a59]/30 transition-colors group"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-[#bd9a59] mb-6" />
                <h3 className="font-['Playfair_Display'] text-2xl text-white font-medium mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#bd9a59] mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-20"
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white font-medium text-center mb-12">
              How We Work
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              {process.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                  className="relative"
                  data-testid={`process-step-${index}`}
                >
                  <div className="glass-card p-6 rounded-sm h-full">
                    <span className="text-[#bd9a59] text-4xl font-light opacity-50 mb-4 block">
                      {item.step}
                    </span>
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-slate-600" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="glass-card p-8 md:p-12 rounded-sm text-center tracing-border"
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white font-medium mb-4">
              Ready to discuss your project?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-8">
              Book a confidential consultation with one of our partners to explore 
              funding options for your next development or acquisition.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button
                  className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-8 py-6 text-sm font-semibold tracking-wide uppercase transition-all duration-300 shadow-[0_0_20px_rgba(189,154,89,0.3)] hover:shadow-[0_0_30px_rgba(189,154,89,0.5)]"
                  data-testid="services-book-call-btn"
                >
                  Book a Partner Call
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-8 py-6 text-sm font-semibold tracking-wide uppercase transition-all"
                  data-testid="services-contact-btn"
                >
                  Send an Enquiry
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
